import React, { cloneElement, createRef, memo } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DataGrid as DataGridMUI, GridLogicOperator } from '@mui/x-data-grid'

import { Logger, LoggerTypes } from '../../../utils'
import { DataGridSearchToolbar } from './DataGridSearchToolbar'
import { DataGridNoRowsOverlay } from './DataGridNoRowsOverlay'
import { DataGridLoadingOverlay } from './DataGridLoadingOverlay'
import './style.scss'

/**
 * Generic DataGrid component.
 *
 * @link https://mui.com/components/data-grid/getting-started/
 * @link https://mui.com/api/data-grid/data-grid/
 * @see {DataGridSearchToolbar}
 *
 * @param {Object} props Ex: {
 *                            actions: [<ButtonIcon {...}/>, <ButtonIcon {...}/>],
 *                            columns: [{}],
 *                            rows: [],
 *                            searchText: '',
 *                            onSearch: (text) => {}
 *                        }
 * @returns {DataGridMUI}
 */
const DataGrid = ({
  actionsButtons,
  isLoading,
  columns,
  columnVisibilityModel,
  rows,
  searchValue,
  onSearch,
  checkboxSelection,
  hideFooterPagination,
  disableColumnFilter,
  exportData,
  onSelectionModelChange,
  onCellEditCommit,
  onCellClick,
  selectionModel
}) => {
  const { t } = useTranslation()
  const ref = createRef()
  const params = useParams()

  if (
    !isLoading &&
    actionsButtons &&
    actionsButtons.length > 0 &&
    !columns.some((el) => Boolean(el.field === 'actions'))
  ) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions'),
      width: 100,
      renderCell: (el) => (
        <div style={{ display: 'inline-flex' }}>
          {actionsButtons.map((ButtonElement, index) =>
            ButtonElement ? (
              cloneElement(ButtonElement, {
                key: `button-datagrid-action-${index}`,
                element: el.row,
                params: params
              })
            ) : (
              <></>
            )
          )}
        </div>
      )
    })
  }

  let exportComponents = null

  if (exportData) {
    exportComponents = {
      toolbar: DataGridSearchToolbar,
      loadingOverlay: DataGridLoadingOverlay,
      noRowsOverlay: DataGridNoRowsOverlay,
      noResultsOverlay: DataGridNoRowsOverlay
    }
  }

  const _onSelectionModelChange = (rowIds) => {
    let currentSelectedRowId = null
    selectionModel.forEach((id) => {
      if (!rowIds.indexOf(id)) {
        currentSelectedRowId = id
      }
    })

    Logger.trace(LoggerTypes.CONTROLLER, 'DataGrid._onSelectionModelChange()', {
      selectionModel,
      rows,
      rowIds,
      currentSelectedRowId
    })

    return onSelectionModelChange(rowIds, currentSelectedRowId)
  }

  const _onRowClick = (_params, event, details) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'DataGrid._onRowClick()', {
      _params,
      event,
      details
    })
  }

  const _onRowSelected = (_params, event, details) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'DataGrid._onRowSelected()', {
      _params,
      event,
      details
    })
  }

  const _onCellClick = (_params, event, details) => {
    // event.stopPropagation()
    // event.preventDefault()

    Logger.trace(LoggerTypes.CONTROLLER, 'DataGrid._onCellClick()', {
      _params,
      event,
      details
    })

    onCellClick(_params, event, details)
  }

  if (!isLoading) {
    Logger.trace(LoggerTypes.CONTAINER, 'DataGrid.render()', { columns, rows, searchValue, selectionModel })
  }

  return (
    <div style={{ minHeight: '400px', height: '100%', width: '100%' }}>
      <DataGridMUI
        initialState={{
          filter: {
            filterModel: {
              items: [],
              quickFilterLogicOperator: GridLogicOperator.And
            }
          }
          // pagination: { paginationModel: { page: 0, pageSize: 10 } }
        }}
        ref={ref}
        // --- Columns ---
        columns={columns}
        columnBuffer={2}
        columnVisibilityModel={columnVisibilityModel}
        ColumnMenuIcon={<div />}
        columnThreshold={2}
        disableColumnMenu={true}
        disableColumnSelector={false}
        // --- Rows ---
        rows={rows}
        rowSelection={checkboxSelection}
        rowSelectionModel={selectionModel}
        isRowSelectable={() => checkboxSelection} // <-- Mandatory to allow checkboxes to be selectable inside the cells
        onRowSelectionModelChange={_onSelectionModelChange}
        onRowSelected={_onRowSelected}
        onRowClick={_onRowClick}
        disableRowSelectionOnClick={!checkboxSelection}
        disableMultipleRowSelection={!checkboxSelection}
        noRowsOverlay={DataGridNoRowsOverlay}
        // --- Cells ---
        onCellEditCommit={onCellEditCommit}
        onCellClick={_onCellClick}
        // disableClickEventBubbling={checkboxSelection}
        // --- Filters ---
        disableColumnFilter={disableColumnFilter}
        filterMode={'client'} // 'client' | 'server'
        // --- Pagination ---
        autoPageSize={false}
        hideFooterPagination={hideFooterPagination}
        pageSizeOptions={[100]}
        paginationMode={'client'} // 'client' | 'server'
        // paginationModel={{ page: 0, pageSize: 100 }}
        // --- Options ---
        checkboxSelection={checkboxSelection}
        density={'compact'} // 'comfortable' | 'compact' | 'standard'
        disableDensitySelector={true}
        disableVirtualization={false}
        editMode={'cell'} // Controls whether to use the 'cell' or 'row' editing.
        loading={isLoading}
        loadingOverlay={DataGridLoadingOverlay}
        noResultsOverlay={DataGridNoRowsOverlay}
        // --- Others ---
        slots={exportComponents}
        slotsProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 250 }
          }
        }}
        componentsProps={{
          toolbar: {
            searchValue: searchValue ? searchValue : '',
            onSearch: (event) => onSearch(event.target.value),
            onClearSearch: () => onSearch('')
          }
        }}
      />
    </div>
  )
}

DataGrid.displayName = 'DataGrid'

DataGrid.propTypes = {
  actionsButtons: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  columnVisibilityModel: PropTypes.object,
  selectionModel: PropTypes.array,
  rows: PropTypes.any.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  onSelectionModelChange: PropTypes.func,
  onCellEditCommit: PropTypes.func,
  onCellClick: PropTypes.func,
  isLoading: PropTypes.bool,
  hideFooterPagination: PropTypes.bool,
  disableColumnFilter: PropTypes.bool,
  exportData: PropTypes.bool,
  checkboxSelection: PropTypes.bool
}

DataGrid.defaultProps = {
  actionsButtons: [],
  columns: [],
  columnVisibilityModel: {},
  selectionModel: [],
  rows: [],
  searchValue: '',
  onSearch: () => {},
  onSelectionModelChange: () => {},
  onCellEditCommit: () => {},
  onCellClick: () => {},
  hideFooterPagination: false,
  disableColumnFilter: false,
  exportData: true,
  checkboxSelection: false
}

export default memo(DataGrid)
